import React, { useRef, useEffect, useState } from 'react';
import { Box, CircularProgress, Grid, Stack, styled, Typography, useTheme } from '@mui/material';
import { t } from 'i18next';
import Timer from '@shared/components/timer/timer';
import { STOP_AUTOSCROLL_THRESHOLD_PX } from '../constants';
import { ConversationListMessage } from '../ConversationListMessage';
import { CallActors } from '@shared/constants/recipient-status.constants';
import { IconDownload } from '@tabler/icons-react';
import { ActionTableButton } from '@shared/components/actionTableButton/actionTableButton';
import type { DocMessage } from '@features/Admin/Campaign/model/type';
import { downloadDialogAsPdf } from '@features/Admin/Campaign/utils/js-pdf.util';
import { useConversationMessageListControllerGetConversationsQuery } from '@shared/services/conversationApiService/apiService';
import { useStyles } from '../styles';
import { ConversationContentProps } from '../types';
import { useConversationMessages, useCallDetails } from '../hooks';
import { WrappedSentimentFlag } from './WrappedSentimentFlag';
import { CopyCallIdButton } from '@shared/components/CopyCallIdButton';
import { ConversationMinimap } from './ConversationMinimap';
import { getActiveTranscriptIndex, getTranscriptSeekOffset } from '@features/CallDetail';

const StyledCallInfoStack = styled(Stack)(() => ({
  display: 'flex',
  justifyContent: 'space-between',
}));

/* eslint-disable react/prop-types */
export const ConversationContent: React.FC<ConversationContentProps> = ({
  call,
  phoneNumber,
  startCallTime,
  onError,
  isRoomConnected,
  playbackPositionSeconds,
  recordingDurationSeconds,
  onSeekToRecordingPosition,
  showCallSummary = true,
  fitAvailableHeight = false,
  hideDownloadButton = false,
  scrollToBottom = true,
}) => {
  const { classes } = useStyles({
    messagesHeight: fitAvailableHeight ? null : 385,
  });
  const theme = useTheme();
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const messagesBoxRef = useRef<HTMLDivElement | null>(null);
  const messageRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const [selectedMessageId, setSelectedMessageId] = useState<string | null>(null);

  const { callDuration } = useCallDetails(call, startCallTime);
  const { sentiments, id: callId } = call;

  const { data: conversationDialog } = useConversationMessageListControllerGetConversationsQuery(
    { conversationId: call.id },
    { skip: hideDownloadButton },
  );
  const transcript = conversationDialog as DocMessage[] | undefined;

  const { allMessages, loading } = useConversationMessages(callId, isRoomConnected, onError);
  const hasMessages = allMessages.length > 0;
  const isActiveCall = call.status !== 'ended';
  const showWaitingState = !hasMessages && (loading || isActiveCall);

  const effectiveAudioDuration =
    recordingDurationSeconds !== undefined && recordingDurationSeconds > 0
      ? recordingDurationSeconds
      : undefined;
  const hasPlaybackState =
    effectiveAudioDuration !== undefined &&
    playbackPositionSeconds !== undefined &&
    Number.isFinite(playbackPositionSeconds);
  const activeMessageIndex = hasPlaybackState
    ? getActiveTranscriptIndex(
        allMessages,
        playbackPositionSeconds ?? 0,
        call.startedAt,
        effectiveAudioDuration,
      )
    : null;

  useEffect(() => {
    setSelectedMessageId(null);
  }, [callId]);

  useEffect(() => {
    if (activeMessageIndex !== null) {
      setSelectedMessageId(allMessages[activeMessageIndex]?.id ?? null);
    }
  }, [activeMessageIndex, allMessages]);

  useEffect(() => {
    if (!scrollToBottom) return;
    if (!messagesBoxRef.current || !messagesEndRef.current) return;

    const { clientHeight, scrollTop, scrollHeight } = messagesBoxRef.current;
    const isNearBottom = scrollHeight - STOP_AUTOSCROLL_THRESHOLD_PX <= clientHeight + scrollTop;

    if (isNearBottom) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [allMessages, scrollToBottom]);

  useEffect(() => {
    if (!scrollToBottom) return;
    if (!messagesEndRef.current) return;

    if (!loading) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [loading, scrollToBottom]);

  const getcallStatusLine = (title: string, value: string | React.ReactNode) => (
    <StyledCallInfoStack direction="row" gap={1}>
      <Typography color={theme.palette.extendedGrey?.darker} fontWeight="500" fontSize="12px">
        {title}:
      </Typography>
      <Box color={theme.palette.extendedGrey?.darker} fontWeight="500" fontSize="12px">
        {value}
      </Box>
    </StyledCallInfoStack>
  );

  return (
    <Grid container className={classes.dialogGridContent} id="dialog-grid-content">
      {showCallSummary && (
        <>
          <Grid item xs={12}>
            {callDuration && getcallStatusLine(t('campaignPage.duration'), callDuration)}
            {startCallTime &&
              callDuration &&
              getcallStatusLine(
                t('campaignPage.duration'),
                <Timer startTimestamp={startCallTime} />,
              )}
          </Grid>
          <Grid item xs={12}>
            {callDuration &&
              getcallStatusLine(
                t('campaignPage.sentiment'),
                <WrappedSentimentFlag sentiments={sentiments} />,
              )}
            {startCallTime &&
              callDuration &&
              getcallStatusLine(
                t('campaignPage.sentiment'),
                <WrappedSentimentFlag sentiments={sentiments} />,
              )}
          </Grid>
        </>
      )}
      {(showCallSummary || !hideDownloadButton) && (
        <Grid item xs={12}>
          <Box
            display={'flex'}
            justifyContent={'space-between'}
            alignItems={'center'}
            gap={1}
            flexWrap={'wrap'}
            sx={{ mt: 1 }}>
            {showCallSummary && (
              <Box display={'flex'} alignItems={'center'} gap={1} flexWrap={'wrap'}>
                <CopyCallIdButton callId={callId} showLabel />
              </Box>
            )}
            {!hideDownloadButton && (
              <ActionTableButton
                disabled={!transcript?.length}
                onClick={() => {
                  if (!transcript?.length) return;
                  const number = call.customer?.number;
                  downloadDialogAsPdf(number || CallActors.CUSTOMER, transcript);
                }}>
                <IconDownload />
              </ActionTableButton>
            )}
          </Box>
        </Grid>
      )}
      <Grid item xs={12} mt="10px">
        <Box className={classes.chatBox}>
          {!loading && hasMessages ? (
            <Box sx={{ position: 'relative', height: '100%', minHeight: 0 }}>
              <Box ref={messagesBoxRef} className={classes.chatBoxMessages}>
                {allMessages.map((messageItem, index) => {
                  let actorName: string | undefined;
                  if (messageItem.actor === CallActors.AGENT) {
                    actorName = undefined;
                  } else if (messageItem.actor === CallActors.SUPERVISOR) {
                    // Named, not left to fall through to the customer label: these lines
                    // were spoken by the operator who took the call over.
                    actorName = t('supervisor.transcriptActor');
                  } else if ((messageItem as any).isMonitor) {
                    actorName = 'Monitor';
                  } else if ((messageItem as any).isLive) {
                    actorName = `${phoneNumber} (Live)`;
                  } else {
                    actorName = phoneNumber;
                  }

                  const offset = getTranscriptSeekOffset(
                    messageItem.createdAt,
                    call.startedAt,
                    effectiveAudioDuration,
                  );
                  const isSeekable =
                    Boolean(onSeekToRecordingPosition) &&
                    effectiveAudioDuration !== undefined &&
                    offset !== null;
                  const isActive =
                    selectedMessageId === messageItem.id || activeMessageIndex === index;
                  const handleActivate = () => {
                    if (!isSeekable || offset === null) return;
                    setSelectedMessageId(messageItem.id);
                    onSeekToRecordingPosition?.(offset);
                  };

                  return (
                    <Box
                      key={messageItem.id || index}
                      ref={(node: HTMLDivElement | null) => {
                        messageRefs.current[messageItem.id] = node;
                      }}>
                      <ConversationListMessage
                        actorName={actorName}
                        messageItem={messageItem}
                        isStacked={messageItem.actor === allMessages[index - 1]?.actor}
                        isActive={isActive}
                        isSeekable={isSeekable}
                        onActivate={handleActivate}
                      />
                    </Box>
                  );
                })}
                <div ref={messagesEndRef} />
              </Box>
              {allMessages.length >= 8 && (
                <ConversationMinimap
                  messages={allMessages}
                  messageRefs={messageRefs}
                  messagesBoxRef={messagesBoxRef}
                />
              )}
            </Box>
          ) : (
            <Box
              display="flex"
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
              height="400px">
              {showWaitingState ? (
                <>
                  <CircularProgress />
                  <Typography sx={{ textAlign: 'center', mt: 2 }}>
                    {t('campaignPage.conversationNotStartedYet')}
                  </Typography>
                </>
              ) : (
                <Typography sx={{ textAlign: 'center' }}>
                  {t('campaignPage.emptyConversationHistory')}
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </Grid>
    </Grid>
  );
};
/* eslint-enable react/prop-types */
